import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, MapPin, Navigation, ChevronUp, Apple, Clock, Phone, Locate, Globe, Building } from "lucide-react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { appleStores, AppleStoreType } from "@/data/appleStores";
import { expandSearchQuery, getSmartSuggestions, getStoresByLocation, StoreSuggestion } from "@/data/storeKeywords";
import { BottomNavigation } from "@/components/BottomNavigation";
import { MenuDrawer } from "@/components/MenuDrawer";

const AppleStore = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const userMarkerRef = useRef<mapboxgl.Marker | null>(null);
  
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStore, setSelectedStore] = useState<AppleStoreType | null>(null);
  const [showDirectionsModal, setShowDirectionsModal] = useState(false);
  const [filteredStores, setFilteredStores] = useState(appleStores);
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [showHeroAnimation, setShowHeroAnimation] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserId(data.user?.id || null);
    });
  }, []);
  
  // Get smart suggestions based on search query
  const suggestions = useMemo(() => {
    return getSmartSuggestions(searchQuery, 8);
  }, [searchQuery]);
  
  // Check if coming from mini map
  const fromMiniMap = location.state?.fromMiniMap;
  const initialUserLocation = location.state?.userLocation;

  // Fetch Mapbox token from edge function
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("get-mapbox-token");
        if (error) throw error;
        setMapboxToken(data.token);
      } catch (err) {
        console.error("Error fetching Mapbox token:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchToken();
  }, []);

  // Get user geolocation
  const getUserLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords: [number, number] = [position.coords.longitude, position.coords.latitude];
          setUserLocation(coords);
          
          if (map.current) {
            // Remove old user marker
            if (userMarkerRef.current) {
              userMarkerRef.current.remove();
            }
            
            // Add user marker
            const el = document.createElement("div");
            el.innerHTML = `
              <div class="relative">
                <div class="w-6 h-6 bg-blue-500 rounded-full border-3 border-white shadow-lg animate-pulse"></div>
                <div class="absolute inset-0 w-6 h-6 bg-blue-500 rounded-full animate-ping opacity-30"></div>
              </div>
            `;
            
            userMarkerRef.current = new mapboxgl.Marker(el)
              .setLngLat(coords)
              .addTo(map.current);
            
            map.current.flyTo({
              center: coords,
              zoom: 10,
              duration: 2000,
            });
          }
          
          toast.success("Posizione trovata!");
        },
        (error) => {
          console.error("Geolocation error:", error);
          toast.error("Impossibile ottenere la posizione");
        },
        { enableHighAccuracy: true }
      );
    } else {
      toast.error("Geolocalizzazione non supportata");
    }
  };

  useEffect(() => {
    if (!mapContainer.current || !mapboxToken) return;

    mapboxgl.accessToken = mapboxToken;

    // Use user location from mini map if available, otherwise default
    const initialCenter = initialUserLocation || userLocation || [0, 20];
    const initialZoom = initialUserLocation || userLocation ? 10 : 1.5;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center: initialCenter as [number, number],
      zoom: initialZoom,
      pitch: 45,
      bearing: 0,
      projection: "globe",
    });

    // Add user marker if we have location
    if (initialUserLocation || userLocation) {
      const coords = initialUserLocation || userLocation;
      setTimeout(() => {
        if (map.current && coords) {
          const el = document.createElement("div");
          el.innerHTML = `
            <div class="relative">
              <div class="w-6 h-6 bg-blue-500 rounded-full border-3 border-white shadow-lg animate-pulse"></div>
              <div class="absolute inset-0 w-6 h-6 bg-blue-500 rounded-full animate-ping opacity-30"></div>
            </div>
          `;
          
          userMarkerRef.current = new mapboxgl.Marker(el)
            .setLngLat(coords as [number, number])
            .addTo(map.current);
        }
      }, 500);
    }

    map.current.on("style.load", () => {
      map.current?.setFog({
        color: "rgb(186, 210, 235)",
        "high-color": "rgb(36, 92, 223)",
        "horizon-blend": 0.02,
        "space-color": "rgb(11, 11, 25)",
        "star-intensity": 0.6,
      });

      // Add weather and traffic layers (visible only at zoom > 7)
      if (map.current) {
        // Traffic layer
        map.current.addSource("mapbox-traffic", {
          type: "vector",
          url: "mapbox://mapbox.mapbox-traffic-v1",
        });
        
        map.current.addLayer({
          id: "traffic-layer",
          type: "line",
          source: "mapbox-traffic",
          "source-layer": "traffic",
          minzoom: 7,
          paint: {
            "line-width": 2,
            "line-color": [
              "match",
              ["get", "congestion"],
              "low", "#4CAF50",
              "moderate", "#FFC107",
              "heavy", "#FF5722",
              "severe", "#D32F2F",
              "#888888"
            ],
            "line-opacity": 0.7,
          },
        });

        // Weather radar layer (precipitation)
        map.current.addSource("weather-radar", {
          type: "raster",
          tiles: [
            "https://tile.openweathermap.org/map/precipitation_new/{z}/{x}/{y}.png?appid=9de243494c0b295cca9337e1e96b00e2"
          ],
          tileSize: 256,
        });

        map.current.addLayer({
          id: "weather-radar-layer",
          type: "raster",
          source: "weather-radar",
          minzoom: 7,
          paint: {
            "raster-opacity": 0.5,
          },
        });
      }
    });

    // Toggle weather/traffic visibility based on zoom
    map.current.on("zoom", () => {
      const zoom = map.current?.getZoom() || 0;
      const visibility = zoom > 7 ? "visible" : "none";
      
      if (map.current?.getLayer("traffic-layer")) {
        map.current.setLayoutProperty("traffic-layer", "visibility", visibility);
      }
      if (map.current?.getLayer("weather-radar-layer")) {
        map.current.setLayoutProperty("weather-radar-layer", "visibility", visibility);
      }
    });

    map.current.addControl(
      new mapboxgl.NavigationControl({ visualizePitch: true }),
      "top-right"
    );

    // Add markers for all stores
    appleStores.forEach((store) => {
      const el = document.createElement("div");
      el.className = "apple-store-marker";
      el.innerHTML = `
        <div class="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg cursor-pointer transform transition-all hover:scale-110 border-2 border-gray-100">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-black">
            <path d="M12 20.94c1.5 0 2.75 1.06 4 1.06 3 0 6-8 6-12.22A4.91 4.91 0 0 0 17 5c-2.22 0-4 1.44-5 2-1-.56-2.78-2-5-2a4.9 4.9 0 0 0-5 4.78C2 14 5 22 8 22c1.25 0 2.5-1.06 4-1.06Z"/>
            <path d="M10 2c1 .5 2 2 2 5"/>
          </svg>
        </div>
      `;

      const marker = new mapboxgl.Marker(el)
        .setLngLat(store.coordinates as [number, number])
        .addTo(map.current!);

      el.addEventListener("click", () => {
        setSelectedStore(store);
        map.current?.flyTo({
          center: store.coordinates as [number, number],
          zoom: 14,
          pitch: 60,
          bearing: -20,
          duration: 2000,
        });
      });

      markersRef.current.push(marker);
    });

    return () => {
      map.current?.remove();
    };
  }, [mapboxToken]);

  useEffect(() => {
    const query = searchQuery.toLowerCase().trim();
    
    // If empty query, show all stores
    if (!query) {
      setFilteredStores(appleStores);
      return;
    }
    
    // Expand search query with multilingual synonyms
    const expandedTerms = expandSearchQuery(query);
    
    // Special case: if searching for common Apple-related terms, show all stores
    const appleKeywords = ["apple", "apple store", "store", "stores", "negozio", "negozi", "tutti", "boutique", "shop"];
    if (appleKeywords.some(keyword => query === keyword)) {
      setFilteredStores(appleStores);
      return;
    }
    
    // Filter by name, city, country, or address using expanded terms
    const filtered = appleStores.filter((store) => {
      const searchText = `${store.name} ${store.city} ${store.country} ${store.address}`.toLowerCase();
      
      // Check if any of the expanded terms match the store data
      const hasMatch = expandedTerms.some(term => {
        // Direct substring match
        if (searchText.includes(term)) return true;
        
        // Check individual words in the term
        const termWords = term.split(/\s+/).filter(w => w.length > 1);
        if (termWords.length > 0) {
          return termWords.every(word => searchText.includes(word));
        }
        
        return false;
      });
      
      return hasMatch;
    });
    
    setFilteredStores(filtered);
  }, [searchQuery]);

  const handleStoreSelect = (store: AppleStoreType) => {
    setSelectedStore(store);
    setIsSearchOpen(false);
    setSearchQuery("");
    setShowSuggestions(true);
    map.current?.flyTo({
      center: store.coordinates as [number, number],
      zoom: 14,
      pitch: 60,
      bearing: -20,
      duration: 2000,
    });
  };

  const handleSuggestionSelect = (suggestion: StoreSuggestion) => {
    if (suggestion.type === "store" && suggestion.store) {
      handleStoreSelect(suggestion.store);
    } else if (suggestion.type === "city" || suggestion.type === "country") {
      const stores = getStoresByLocation(suggestion.type, suggestion.text);
      setFilteredStores(stores);
      setShowSuggestions(false);
      setSearchQuery(suggestion.text);
      
      // Zoom to first store of that location
      if (stores.length > 0 && map.current) {
        const bounds = new mapboxgl.LngLatBounds();
        stores.forEach(store => {
          bounds.extend(store.coordinates as [number, number]);
        });
        map.current.fitBounds(bounds, {
          padding: 100,
          duration: 2000,
        });
      }
    }
  };

  const openDirections = (app: "apple" | "google") => {
    if (!selectedStore) return;
    const [lng, lat] = selectedStore.coordinates;
    
    if (app === "apple") {
      window.open(`http://maps.apple.com/?daddr=${lat},${lng}&dirflg=d`, "_blank");
    } else {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, "_blank");
    }
    setShowDirectionsModal(false);
  };

  // Handle hero animation from mini map
  useEffect(() => {
    if (fromMiniMap) {
      setShowHeroAnimation(true);
      // Set initial user location from mini map
      if (initialUserLocation) {
        setUserLocation(initialUserLocation);
      }
      // Clear the animation after it completes
      const timer = setTimeout(() => {
        setShowHeroAnimation(false);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [fromMiniMap, initialUserLocation]);

  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 bg-background"
      >
        <div className="fixed inset-0 bg-background flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="flex flex-col items-center gap-4"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 border-3 border-primary border-t-transparent rounded-full"
            />
            <p className="text-muted-foreground">Caricamento mappa...</p>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      initial={fromMiniMap ? { 
        opacity: 0,
        scale: 0.95,
      } : { opacity: 0 }}
      animate={{ 
        opacity: 1,
        scale: 1,
      }}
      transition={{ 
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      className="fixed inset-0 bg-background overflow-hidden will-change-transform"
    >
      <div className="fixed inset-0 bg-background">
        {/* Map Container */}
        <div ref={mapContainer} className="absolute inset-0" />

        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          onClick={() => navigate("/")}
          className="absolute top-4 left-4 z-50 p-3 bg-background/80 backdrop-blur-lg rounded-full shadow-lg border border-border/50"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <X className="w-5 h-5 text-foreground" />
        </motion.button>

        {/* Title - Left aligned */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="absolute top-4 left-16 z-50"
        >
          <div className="flex items-center gap-2 px-4 py-2 bg-background/80 backdrop-blur-lg rounded-full shadow-lg border border-border/50">
            <Apple className="w-5 h-5 text-foreground" />
            <span className="font-semibold text-foreground">Apple Store</span>
          </div>
        </motion.div>

        {/* Geolocation Button - Under zoom controls */}
        <motion.button
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.25 }}
          onClick={getUserLocation}
          className="absolute top-[140px] right-[10px] z-50 p-2.5 bg-background/90 backdrop-blur-lg rounded-lg shadow-lg border border-border/50"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <Locate className="w-5 h-5 text-foreground" />
        </motion.button>

        {/* Search Bar - Centered, above Mapbox logo */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="absolute bottom-24 left-0 right-0 z-50 flex justify-center px-4"
        >
          <motion.button
            onClick={() => setIsSearchOpen(true)}
            className="w-full max-w-md flex items-center justify-center gap-3 px-5 py-4 bg-background/90 backdrop-blur-lg rounded-2xl shadow-xl border border-border/50"
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            <Search className="w-5 h-5 text-muted-foreground" />
            <span className="text-muted-foreground">Cerca Apple Store...</span>
            <ChevronUp className="w-5 h-5 text-muted-foreground ml-auto" />
          </motion.button>
        </motion.div>

        {/* Search Panel */}
        <AnimatePresence>
          {isSearchOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] bg-background/[0.78] backdrop-blur-xl"
            >
              <motion.div
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="h-full flex flex-col" style={{ height: "100dvh" }}
              >
                {/* Search Header */}
                <div className="flex items-center gap-3 p-4 border-b border-border/50">
                  <motion.button
                    onClick={() => {
                      setIsSearchOpen(false);
                      setSearchQuery("");
                    }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="p-2 rounded-full hover:bg-muted/50 transition-colors"
                  >
                    <X className="w-5 h-5 text-foreground" />
                  </motion.button>
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Cerca negozio, città, paese... (multilingua)"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setShowSuggestions(true);
                      }}
                      autoFocus
                      className="w-full pl-10 pr-4 py-3 bg-muted/50 rounded-xl border-none outline-none focus:ring-2 focus:ring-primary/50 text-foreground placeholder:text-muted-foreground"
                    />
                  </div>
                </div>

                {/* Smart Suggestions */}
                <AnimatePresence>
                  {showSuggestions && suggestions.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="px-4 py-2 border-b border-border/30"
                    >
                      <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        Suggerimenti intelligenti
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {suggestions.map((suggestion, index) => (
                          <motion.button
                            key={`${suggestion.type}-${suggestion.text}-${index}`}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: index * 0.03 }}
                            onClick={() => handleSuggestionSelect(suggestion)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all duration-200 ${
                              suggestion.type === "city" 
                                ? "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/30"
                                : suggestion.type === "country"
                                ? "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/30"
                                : "bg-muted/50 text-foreground hover:bg-muted/70 border border-border/30"
                            }`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            {suggestion.type === "city" && <Building className="w-3 h-3" />}
                            {suggestion.type === "country" && <Globe className="w-3 h-3" />}
                            {suggestion.type === "store" && <Apple className="w-3 h-3" />}
                            <span className="font-medium">{suggestion.text}</span>
                            {suggestion.count && (
                              <span className="text-xs opacity-70">({suggestion.count})</span>
                            )}
                          </motion.button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Store Count */}
                <div className="px-4 py-2 text-sm text-muted-foreground flex items-center justify-between">
                  <span>{filteredStores.length} Apple Store trovati</span>
                  {searchQuery && (
                    <button
                      onClick={() => {
                        setSearchQuery("");
                        setFilteredStores(appleStores);
                        setShowSuggestions(true);
                      }}
                      className="text-xs text-primary hover:underline"
                    >
                      Mostra tutti
                    </button>
                  )}
                </div>

                {/* Results List */}
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 pb-32" style={{ WebkitOverflowScrolling: "touch" }}>

                  <div className="space-y-3">
                    {filteredStores.map((store, index) => (
                      <motion.button
                        key={store.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.02 }}
                        onClick={() => handleStoreSelect(store)}
                        className="w-full flex items-center gap-4 p-4 bg-muted/30 hover:bg-muted/50 rounded-2xl transition-all duration-300 group"
                        whileHover={{ x: 8 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
                          <img
                            src={store.image}
                            alt={store.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                        <div className="flex-1 text-left">
                          <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                            {store.name}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {store.city}, {store.country}
                          </p>
                        </div>
                        <MapPin className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                      </motion.button>
                    ))}
                    {filteredStores.length === 0 && (
                      <div className="text-center py-12 text-muted-foreground">
                        <Globe className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>Nessun Apple Store trovato</p>
                        <p className="text-xs mt-1">Prova a cercare in un'altra lingua</p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Store Detail Card */}
        <AnimatePresence>
          {selectedStore && !isSearchOpen && (
            <motion.div
              initial={{ opacity: 0, y: 100, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 100, scale: 0.9 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed bottom-24 left-4 right-4 z-50 md:left-1/2 md:-translate-x-1/2 md:w-[400px]"
            >
              <div className="bg-background/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-border/50 overflow-hidden">
                {/* Store Image */}
                <div className="relative h-48 overflow-hidden">
                  <motion.img
                    src={selectedStore.image}
                    alt={selectedStore.name}
                    className="w-full h-full object-cover"
                    initial={{ scale: 1.2 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.6 }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
                  <motion.button
                    onClick={() => setSelectedStore(null)}
                    className="absolute top-3 right-3 p-2 bg-background/80 backdrop-blur-sm rounded-full"
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <X className="w-4 h-4 text-foreground" />
                  </motion.button>
                </div>

                {/* Store Info */}
                <div className="p-5">
                  <motion.h2
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-xl font-bold text-foreground mb-1"
                  >
                    {selectedStore.name}
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="text-sm text-muted-foreground mb-4"
                  >
                    {selectedStore.address}
                  </motion.p>

                  {/* Hours and Phone */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.18 }}
                    className="flex flex-col gap-2 mb-4"
                  >
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      <span>{selectedStore.hours}</span>
                    </div>
                    <a 
                      href={`tel:${selectedStore.phone}`}
                      className="flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <Phone className="w-4 h-4" />
                      <span>{selectedStore.phone}</span>
                    </a>
                  </motion.div>

                  {/* Directions Button */}
                  <motion.button
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    onClick={() => setShowDirectionsModal(true)}
                    className="w-full flex items-center justify-center gap-3 py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-2xl font-medium transition-all duration-300"
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Navigation className="w-5 h-5" />
                    <span>Indicazioni</span>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Directions Modal */}
        <AnimatePresence>
          {showDirectionsModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-background/80 backdrop-blur-lg"
              onClick={() => setShowDirectionsModal(false)}
            >
              <motion.div
                initial={{ scale: 0.8, y: 50 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.8, y: 50 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-sm bg-background rounded-3xl shadow-2xl border border-border/50 overflow-hidden"
              >
                <div className="p-6">
                  <h3 className="text-xl font-bold text-center text-foreground mb-2">
                    Apri con
                  </h3>
                  <p className="text-sm text-center text-muted-foreground mb-6">
                    Scegli l'app per le indicazioni
                  </p>

                  <div className="space-y-3">
                    <motion.button
                      onClick={() => openDirections("apple")}
                      className="w-full flex items-center gap-4 p-4 bg-muted/30 hover:bg-muted/50 rounded-2xl transition-all duration-300 group"
                      whileHover={{ scale: 1.02, x: 4 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-white" />
                      </div>
                      <span className="font-medium text-foreground">Apple Maps</span>
                    </motion.button>

                    <motion.button
                      onClick={() => openDirections("google")}
                      className="w-full flex items-center gap-4 p-4 bg-muted/30 hover:bg-muted/50 rounded-2xl transition-all duration-300 group"
                      whileHover={{ scale: 1.02, x: 4 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-400 via-green-400 to-yellow-400 rounded-xl flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-white" />
                      </div>
                      <span className="font-medium text-foreground">Google Maps</span>
                    </motion.button>
                  </div>

                  <motion.button
                    onClick={() => setShowDirectionsModal(false)}
                    className="w-full mt-4 py-3 text-muted-foreground hover:text-foreground transition-colors"
                    whileTap={{ scale: 0.98 }}
                  >
                    Annulla
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bottom Navigation */}
        {userId && (
          <>
            <BottomNavigation onMenuClick={() => setIsMenuOpen(true)} />
            <MenuDrawer open={isMenuOpen} onOpenChange={setIsMenuOpen} />
          </>
        )}
      </div>
    </motion.div>
  );
};

export default AppleStore;
